// src/Medicalcase/components/CreateCase/CreateCaseForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSpecialtyLabel } from "../Specialtyfilter/specialtyLabel";
// ↑ Adaptez ce chemin si CreateCaseForm et Specialtyfilter ne partagent
// pas le même dossier parent (components/).
import "./CreateCaseForm.css";

const IChevron = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Repli si le parent ne fournit aucune spécialité : ce sont des VALEURS
// ANGLAISES (mêmes clés que dans SPECIALTIES / specialties.json), jamais
// traduites en dur ici. L'affichage passe toujours par getSpecialtyLabel.
const DEFAULT_SPECIALTIES = ["General", "Cardiology", "Neurology"];

export default function CreateCaseForm({ open, onClose, onCreate, specialties = [], isPage = false }) {
  const { t, i18n } = useTranslation();

  // Direction du texte selon la langue active (ar => rtl),
  // même logique que dans SpecialtyFilter.jsx / GoLiveStreamForm.jsx.
  const isRTL = i18n.dir(i18n.language) === "rtl";

  const fallbackSpecialties = useMemo(() => {
    const normalized = (specialties || [])
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);

    return normalized.length > 0 ? normalized : DEFAULT_SPECIALTIES;
  }, [specialties]);

  // Liste finale : on garde la VALEUR brute (utilisée pour le formulaire /
  // l'API) et on calcule le LIBELLÉ traduit via getSpecialtyLabel, exactement
  // comme dans SpecialtyFilter.jsx et GoLiveStreamForm.jsx.
  const specialtyOptions = useMemo(
    () =>
      fallbackSpecialties.map((value) => ({
        value,
        label: getSpecialtyLabel(t, value),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fallbackSpecialties, t, i18n.language]
  );

  const [title, setTitle] = useState("");
  const [specialty, setSpecialty] = useState(fallbackSpecialties[0] || "");
  const [description, setDescription] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [submittedMessage, setSubmittedMessage] = useState("");
  const [isSpecialtyOpen, setIsSpecialtyOpen] = useState(false);

  const comboRef = useRef(null);

  // Si la liste de spécialités change (ex: prop mise à jour) et que la
  // valeur sélectionnée n'en fait plus partie, on retombe sur la première.
  useEffect(() => {
    if (!specialty || !fallbackSpecialties.includes(specialty)) {
      setSpecialty(fallbackSpecialties[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackSpecialties]);

  // Ferme la liste déroulante au clic en dehors ou à la touche Échap
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboRef.current && !comboRef.current.contains(event.target)) {
        setIsSpecialtyOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsSpecialtyOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (!open) return null;

  const handleFiles = (e) => {
    setAttachments(Array.from(e.target.files || []));
  };

  const attachmentNames = useMemo(
    () => attachments.map((file) => file?.name || t("createCase.fileFallback", { defaultValue: "File" })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachments, t]
  );

  const selectedSpecialtyLabel = useMemo(() => {
    const found = specialtyOptions.find((opt) => opt.value === specialty);
    return found ? found.label : "";
  }, [specialtyOptions, specialty]);

  const handleSelectSpecialty = (value) => {
    setSpecialty(value);
    setIsSpecialtyOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSubmittedMessage("");

    if (!title.trim()) return setError(t("createCase.titleRequired"));
    if (!description.trim()) return setError(t("createCase.descriptionRequired"));

    const selectedSpecialty = specialty || fallbackSpecialties[0] || "General";

    const formData = {
      title: title.trim(),
      specialty: selectedSpecialty,
      description: description.trim(),
      privacy,
      attachments,
      payload: {
        caseTitle: title.trim(),
        medicalSpeciality: selectedSpecialty,
        caseDescription: description.trim(),
        privacySetting: privacy,
      },
    };

    if (onCreate) onCreate(formData);
    setSubmittedMessage(t("createCase.createButton", { defaultValue: "Case submitted" }));
  };

  // Menu déroulant personnalisé pour la spécialité : hauteur réduite
  // (quelques options visibles) + scroll interne pour le reste, même
  // principe que mcd-live-form__combo dans GoLiveStreamForm.
  const specialtyCombo = (
    <div className="mcd-create-case__field-group">
      <label className="mcd-create-case__label">{t("createCase.specialty")}</label>
      <div className="mcd-create-case__combo" ref={comboRef}>
        <button
          type="button"
          className="mcd-create-case__combo-trigger"
          onClick={() => setIsSpecialtyOpen((open) => !open)}
          aria-haspopup="listbox"
          aria-expanded={isSpecialtyOpen}
        >
          <span className={selectedSpecialtyLabel ? "" : "mcd-create-case__combo-placeholder"}>
            {selectedSpecialtyLabel || t("createCase.selectSpecialty", { defaultValue: "Select specialty" })}
          </span>
          <span className={`mcd-create-case__combo-chevron${isSpecialtyOpen ? " is-open" : ""}`}>
            <IChevron />
          </span>
        </button>

        {isSpecialtyOpen && (
          <ul
            className="mcd-create-case__combo-list"
            role="listbox"
            aria-label={t("createCase.specialty")}
          >
            {specialtyOptions.map((item) => (
              <li
                key={item.value}
                role="option"
                aria-selected={specialty === item.value}
                className={`mcd-create-case__combo-option${specialty === item.value ? " is-selected" : ""}`}
                onClick={() => handleSelectSpecialty(item.value)}
              >
                {item.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const formBody = (
    <form className="mcd-create-case__form" onSubmit={handleSubmit}>
      <label className="mcd-create-case__label">{t("createCase.caseTitle")}</label>
      <input
        className="mcd-create-case__input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t("createCase.caseTitlePlaceholder")}
      />

      {specialtyCombo}

      <label className="mcd-create-case__label">{t("createCase.description")}</label>
      <textarea
        className="mcd-create-case__textarea"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t("createCase.descriptionPlaceholder")}
        rows={6}
      />

      <label className="mcd-create-case__label">{t("createCase.attachmentsLabel")}</label>
      <div className="mcd-create-case__dropzone">
        <div className="mcd-create-case__drop-inner">
          <div className="mcd-create-case__drop-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12" />
              <path d="m17 8-5-5-5 5" />
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            </svg>
          </div>
          <div className="mcd-create-case__drop-text">{t("createCase.attachmentsText")}</div>
          <label className="mcd-create-case__browse">
            {t("createCase.browseFiles")}
            <input className="mcd-create-case__file" type="file" multiple onChange={handleFiles} />
          </label>
          <div className="mcd-create-case__drop-sub">{t("createCase.attachmentsSubtext")}</div>
          {attachmentNames.length > 0 ? (
            <div className="mcd-create-case__attachments-list">{attachmentNames.join(", ")}</div>
          ) : null}
        </div>
      </div>

      <div className="mcd-create-case__privacy">
        <label>
          <input type="radio" name="privacy" value="public" checked={privacy === "public"} onChange={() => setPrivacy("public")} /> {t("createCase.public")}
        </label>
        <label>
          <input type="radio" name="privacy" value="private" checked={privacy === "private"} onChange={() => setPrivacy("private")} /> {t("createCase.private")}
        </label>
      </div>

      {error && <div className="mcd-create-case__error">{error}</div>}
      {submittedMessage && <div className="mcd-create-case__success">{submittedMessage}</div>}

      <div className="mcd-create-case__actions">
        <button type="button" className="mcd-create-case__btn mcd-create-case__btn--muted" onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button type="submit" className="mcd-create-case__btn mcd-create-case__btn--primary">
          {t("createCase.createButton")}
        </button>
      </div>
    </form>
  );

  if (isPage) {
    return (
      <div className="mcd-create-case__page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="mcd-create-case__card" role="region">
          <div className="mcd-create-case__page-header">
            <button className="mcd-create-case__back" onClick={onClose} type="button" aria-label={t("createCase.back")}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={isRTL ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
              </svg>
            </button>
            <h2 className="mcd-create-case__title">{t("createCase.pageTitle")}</h2>
          </div>
          <div className="mcd-create-case__hipaa">{t("createCase.hipaaNotice")}</div>

          {formBody}
        </div>
      </div>
    );
  }

  return (
    <div className="mcd-create-case__modal" dir={isRTL ? "rtl" : "ltr"}>
      <div className="mcd-create-case__backdrop" onClick={onClose} />
      <div className="mcd-create-case__card" role="dialog" aria-modal="true">
        <h2 className="mcd-create-case__title">{t("createCase.pageTitle")}</h2>
        <div className="mcd-create-case__hipaa">{t("createCase.hipaaNotice")}</div>

        {formBody}
      </div>
    </div>
  );
}